import React, { useState, useCallback } from 'react';
import { PageContainer } from '../components/common/layout/PageContainer';
import { ContactFormModal } from '../components/features/contacts/ContactFormModal';
import { ContactTable } from '../components/features/contacts/ContactTable';
import { ContactTableHeader } from '../components/features/contacts/ContactTableHeader';
import { FilterPanel } from '../components/features/contacts/FilterPanel';
import { EMPTY_FILTER_STATE, type FilterState } from '../components/features/contacts/filterState';
import { AddTagsModal } from '../components/features/contacts/AddTagsModal';
import { useContacts as useContactsData } from '../hooks/graphql/contacts/useContacts';
import { OffsetPagination } from '../components/common/OffsetPagination';
import { TableCard } from '../components/common/TableCard';
import { EditContactModal } from '../components/features/contacts/EditContactModal';
import { useAuthStore } from '../contexts/auth/useAuthStore';
import { UploadDatabaseModal } from '../components/features/flows/UploadDatabaseModal';
import { useHasIntegration } from '../hooks/useHasIntegration';
import type { ContactFormData } from '../schemas/contact.schema';

const Contact: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddTagsModalOpen, setIsAddTagsModalOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<{ id: string } | null>(null);
  const [showUploadDatabaseModal, setShowUploadDatabaseModal] = useState(false);
  useAuthStore();
  const hasIntegration = useHasIntegration();

  const _handleOpenEditModal = (contact: { id: string }) => {
    // Just pass the contact ID to the EditContactModal
    // The EditContactModal will fetch the full contact data itself
    setSelectedContact({ id: contact.id });
    setShowEditModal(true);
  };

  const {
    contacts,
    isLoading,
    isCountLoading,
    error,
    totalContacts,
    hasMore,
    searchQuery,
    searchField = '',
    selectedContacts,
    sortField,
    sortDirection,
    pageSize,
    currentPage,
    handleSelectAll,
    handleSelectContact,
    handleSort,
    handleSearch,
    handleDownload,
    handleAddContact,
    handleDeleteContact,
    handleDeleteSelected,
    handleNextPage,
    handlePrevPage,
    handlePageChange,
    handlePageSizeChange,
    handleFilter,
    refetch,
  } = useContactsData();

  const handleEdit = useCallback((_contact: { id: string }) => {
    // TODO: Implement edit logic
  }, []);

  const handleSubmit = useCallback(
    async (data: ContactFormData) => {
      const success = await handleAddContact(data);
      if (success) {
        setIsModalOpen(false);
      }
    },
    [handleAddContact]
  );

  // Owned here rather than in the header: the panel that edits these sits
  // beside the table, so it cannot be a child of the header above it.
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTER_STATE);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  /**
   * Applied on every edit — there is no Apply step. `useContacts` debounces
   * the request, so holding a key down does not fan out into one query per
   * character.
   */
  const handleFiltersChange = (next: FilterState) => {
    setFilters(next);
    handleFilter(next);
  };

  const loadError = error instanceof Error ? error : null;

  // Full width: the filter panel takes ~27rem out of the row, and a capped
  // container left the table cramped with it open. Both the base and the 3xl
  // cap have to be cleared — twMerge treats a breakpoint variant as its own
  // group, so max-w-none alone would leave 3xl:max-w-[1800px] in place.
  return (
    <PageContainer className="py-6 max-w-none 3xl:max-w-none">
      <div className="flex flex-col space-y-6">
        <div className="flex flex-col gap-6">
          <ContactTableHeader
            filters={filters}
            onToggleFilterPanel={() => setIsFilterPanelOpen((wasOpen) => !wasOpen)}
            isFilterPanelOpen={isFilterPanelOpen}
            searchQuery={searchQuery}
            searchField={searchField}
            onSearchChange={handleSearch}
            onRefresh={refetch}
            onDownload={handleDownload}
            onAddContact={() => setIsModalOpen(true)}
            onUploadDatabase={!hasIntegration ? () => setShowUploadDatabaseModal(true) : undefined}
            onFilter={handleFiltersChange}
            isLoading={isLoading}
            sidePanel={
              <FilterPanel
                open={isFilterPanelOpen}
                onClose={() => setIsFilterPanelOpen(false)}
                value={filters}
                onChange={handleFiltersChange}
              />
            }
          >
            <TableCard
              className="overflow-x-clip"
              footer={
                loadError ? undefined : (
                  <OffsetPagination
                    currentPage={currentPage ?? 0}
                    totalItems={totalContacts}
                    pageSize={pageSize}
                    onNextPage={handleNextPage}
                    onPrevPage={handlePrevPage}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    isLoading={isLoading}
                    isTotalLoading={isCountLoading}
                    hasMore={hasMore}
                    label="contacts"
                  />
                )
              }
            >
              {loadError ? (
                <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
                  <p className="text-sm font-medium text-foreground">Could not load contacts.</p>
                  <p className="max-w-md text-xs text-foreground/60">{loadError.message}</p>
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="mt-2 rounded-lg border border-foreground/30 px-3 py-1.5 text-xs hover:bg-muted/60"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <ContactTable
                  contacts={contacts}
                  isLoading={isLoading}
                  selectedContacts={selectedContacts}
                  onSelectAll={handleSelectAll}
                  onSelectContact={handleSelectContact}
                  onEdit={handleEdit}
                  onDelete={handleDeleteContact}
                  onDeleteSelected={handleDeleteSelected}
                  onSort={handleSort}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onAddTags={() => setIsAddTagsModalOpen(true)}
                />
              )}
            </TableCard>
          </ContactTableHeader>
        </div>
      </div>

      <ContactFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
      />

      <AddTagsModal
        isOpen={isAddTagsModalOpen}
        onClose={() => setIsAddTagsModalOpen(false)}
        selectedContacts={selectedContacts}
        onSuccess={refetch}
      />

      <EditContactModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedContact(null);
        }}
        contact={selectedContact}
        onSubmit={async (_data) => {
          setShowEditModal(false);
          setSelectedContact(null);
        }}
        onSuccess={(_data) => {
          // Refresh contacts data after successful edit
          refetch();
        }}
      />

      {/* Upload Database Modal */}
      <UploadDatabaseModal
        isOpen={showUploadDatabaseModal}
        onClose={() => setShowUploadDatabaseModal(false)}
      />
    </PageContainer>
  );
};

export default Contact;
